import React, { useState, useRef } from 'react';

const points = [
  { id: '1', x: 99, y: 150 },
  { id: 'А', x: 211, y: 49 },
  { id: '2', x: 280, y: 123 },
  { id: 'Б', x: 190, y: 128 },
  { id: '3', x: 274, y: 232 },
  { id: 'В', x: 94, y: 276 },
  { id: '4', x: 172, y: 215 },
  { id: 'Г', x: 36, y: 218 },
  { id: '5', x: 23, y: 107 },
  { id: 'Д', x: 98, y: 47 },
];

const sequence = ['1', 'А', '2', 'Б', '3', 'В', '4', 'Г', '5', 'Д'];

// Идеальный путь, который должен нарисовать пациент (начиная с 2)
const EXPECTED_PATH = ['Б', '3', 'В', '4', 'Г', '5', 'Д'];

const getEdgePath = (p1, p2, r = 16) => {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const len = Math.sqrt(dx*dx + dy*dy);
  if (len === 0) return `M${p1.x},${p1.y}`;
  const ux = dx / len;
  const uy = dy / len;
  return `M${p1.x + ux * r},${p1.y + uy * r} L${p2.x - ux * r},${p2.y - uy * r}`;
};

const INITIAL_COMPLETED = ['1', 'А', '2'];
const INITIAL_PATHS = [
  getEdgePath(points[0], points[1]),
  getEdgePath(points[1], points[2])
];

export default function TrailMaking({ onChoiceClick }) {
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPath, setCurrentPath] = useState('');
  const [completedPaths, setCompletedPaths] = useState(INITIAL_PATHS);
  // Точки, которые пациент задел во время рисования (в порядке задания)
  const [touchedPoints, setTouchedPoints] = useState([]);
  const svgRef = useRef(null);

  const getCoords = (e) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    const scaleX = 300 / rect.width;
    const scaleY = 300 / rect.height;
    const clientX = e.clientX || (e.touches && e.touches[0].clientX) || (e.changedTouches && e.changedTouches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY) || (e.changedTouches && e.changedTouches[0].clientY);
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const handleSvgStart = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const { x, y } = getCoords(e);
    setCurrentPath(prev => prev ? prev : `M${x},${y}`);
  };

    const handleMove = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const { x, y } = getCoords(e);
    setCurrentPath(prev => `${prev} L${x},${y}`);

    // Проверяем, не задели ли мы какую-то точку (радиус 24)
    for (let p of points) {
      const dist = Math.sqrt((x - p.x)**2 + (y - p.y)**2);
      if (dist <= 24) {
        // Если точку еще не трогали (не считая 1, А, 2 из примера)
        if (!INITIAL_COMPLETED.includes(p.id) && !touchedPoints.includes(p.id)) {
          const newTouched = [...touchedPoints, p.id];
          setTouchedPoints(newTouched);
          
          // Если дошли до финальной точки 'Д' — тест завершен
          if (p.id === 'Д') {
            setIsDrawing(false);
            const isCorrect = JSON.stringify(newTouched) === JSON.stringify(EXPECTED_PATH);
            
            // Сохраняем картинку в отчет ВСЕГДА (даже при ошибке)
            try {
              const svg = svgRef.current;
              const svgData = new XMLSerializer().serializeToString(svg);
              const dataUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgData);
              // Отправляем текст для бэкенда + картинку через разделитель ||
              const answerText = isCorrect ? 'Задание выполнено верно' : 'Задание выполнено неверно';
              onChoiceClick(`${answerText}||${dataUrl}`);
            } catch (err) {
              console.error("Ошибка сохранения:", err);
              onChoiceClick(isCorrect ? 'correct' : 'incorrect');
            }
          }
        }
      }
    }
  };

  const handleEnd = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    setIsDrawing(false);
  };

  return (
    <div className="flex flex-col items-center gap-6 w-full max-w-4xl">
      <h2 className="text-2xl md:text-3xl font-semibold text-primary text-center min-h-[40px]">
      Соедините точки в возрастающем порядке: 1-А-2 и так далее. Закончите на букве «Д».
      </h2>
      
      <div className="bg-white rounded-2xl shadow-lg border-2 border-border p-2 touch-none">
        <style>
          {`
            .draw-anim { stroke-dasharray: 300; stroke-dashoffset: 300; animation: dash 1.5s ease-in-out forwards; }
            .draw-anim-2 { stroke-dasharray: 300; stroke-dashoffset: 300; animation: dash 1.5s ease-in-out 1.5s forwards; }
            @keyframes dash { to { stroke-dashoffset: 0; } }
          `}
        </style>
        <svg
          ref={svgRef}
          width="300"
          height="300"
          viewBox="0 0 300 300"
          xmlns="http://www.w3.org/2000/svg"
          onMouseDown={handleSvgStart}
          onMouseMove={handleMove}
          onMouseUp={handleEnd}
          onTouchStart={handleSvgStart}
          onTouchMove={handleMove}
          onTouchEnd={handleEnd}
          className="w-[300px] h-[300px] md:w-[400px] md:h-[400px] cursor-crosshair"
          style={{ userSelect: 'none' }}
        >
          <defs>
            <marker id="arrowhead" markerWidth="4" markerHeight="4" refX="3" refY="2" orient="auto">
              <polygon points="0 0, 4 2, 0 4" fill="#4caf50" />
            </marker>
          </defs>

          {/* Точки */}
          {points.map(p => {
            const isExample = INITIAL_COMPLETED.includes(p.id);
            let stroke = '#333'; let fill = 'white';
            if (isExample) { stroke = '#4caf50'; fill = '#c8e6c9'; }
            return (
              <g key={p.id}>
                <circle cx={p.x} cy={p.y} r="16" stroke={stroke} fill={fill} strokeWidth="2.5" pointerEvents="none" />
                <text x={p.x} y={p.y} fontFamily="Arial" fontSize="18" fontWeight="bold" textAnchor="middle" dominantBaseline="middle" pointerEvents="none" fill={isExample ? '#2e7d32' : '#333'}>{p.id}</text>
              </g>
            );
          })}

          {/* Линии примера (1->А, А->2) */}
          <path className="draw-anim" d={INITIAL_PATHS[0]} fill="none" stroke="#4caf50" strokeWidth="3" markerEnd="url(#arrowhead)" />
          <path className="draw-anim-2" d={INITIAL_PATHS[1]} fill="none" stroke="#4caf50" strokeWidth="3" markerEnd="url(#arrowhead)" />

          {/* Текущая линия пациента (свободное рисование) */}
          <path d={currentPath} fill="none" stroke="#00bcd4" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" pointerEvents="none" />
        </svg>
      </div>
    </div>
  );
}